import { featureForPath } from "@/features/service-quality/catalog";
import type { ServiceQualityObservation } from "@/features/service-quality/contracts";
import { capturePostHog } from "./posthog";

const storageKey = "client.service-quality.session.v1";
const visitKey = "client.auxm.visit.v1";
const actionCountKey = "client.auxm.action-counts.v1";
const schemaVersion = 2;

declare const __CLIENT_BUILD_VERSION__: string;
declare const __CLIENT_BUILD_BRANCH__: string;

type EventKind = "view" | "interaction" | "api" | "failure";

export type ApiOperationContext = {
  initiatingFeatureId: string;
  initiatingPathTemplate: string;
};

function readObservations(): Record<string, ServiceQualityObservation> {
  if (typeof sessionStorage === "undefined") return {};
  try {
    return JSON.parse(sessionStorage.getItem(storageKey) ?? "{}") as Record<
      string,
      ServiceQualityObservation
    >;
  } catch {
    return {};
  }
}

function record(
  featureId: string,
  kind: EventKind,
  properties: Record<string, string | number | boolean>,
) {
  const observations = readObservations();
  const current = observations[featureId] ?? {
    featureId,
    views: 0,
    interactions: 0,
    apiOperations: 0,
    apiSuccesses: 0,
    clientFailures: 0,
    durationsMs: [],
  };
  if (kind === "view") current.views += 1;
  if (kind === "interaction") current.interactions += 1;
  if (kind === "failure") current.clientFailures += 1;
  if (kind === "api") {
    current.apiOperations += 1;
    if (properties.success === true) current.apiSuccesses += 1;
    if (typeof properties.duration_ms === "number") {
      current.durationsMs = [
        ...current.durationsMs.slice(-99),
        properties.duration_ms,
      ];
    }
  }
  observations[featureId] = current;
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(observations));
  } catch {
    // Telemetry must never break the product when storage is unavailable.
  }
  capturePostHog(`client_${kind === "api" ? "api_operation" : kind}`, {
    ...commonProperties(),
    feature_id: featureId,
    ...properties,
  });
  window.dispatchEvent(new CustomEvent("client:service-quality"));
}

function visitId() {
  try {
    const existing = sessionStorage.getItem(visitKey);
    if (existing) return existing;
    const created = crypto.randomUUID();
    sessionStorage.setItem(visitKey, created);
    return created;
  } catch {
    return "unavailable";
  }
}

function commonProperties() {
  return {
    schema_version: schemaVersion,
    feature_id: activeFeature(),
    path_template: featureForPath(window.location.pathname)?.path ?? "unknown",
    visit_id: visitId(),
    emitted_at: new Date().toISOString(),
    build_version:
      typeof __CLIENT_BUILD_VERSION__ === "string"
        ? __CLIENT_BUILD_VERSION__
        : "unknown",
    build_branch:
      typeof __CLIENT_BUILD_BRANCH__ === "string"
        ? __CLIENT_BUILD_BRANCH__
        : "unknown",
    synthetic: navigator.webdriver === true,
  };
}

function captureSemantic(
  eventType: string,
  properties: Record<string, string | number | boolean> = {},
) {
  capturePostHog("client_semantic", {
    ...commonProperties(),
    event_type: eventType,
    ...properties,
  });
}

function actionOccurrence(action: string) {
  try {
    const counts = JSON.parse(
      sessionStorage.getItem(actionCountKey) ?? "{}",
    ) as Record<string, number>;
    counts[action] = (counts[action] ?? 0) + 1;
    sessionStorage.setItem(actionCountKey, JSON.stringify(counts));
    return counts[action];
  } catch {
    return 1;
  }
}

export function captureJourney(
  action: string,
  properties: Record<string, string | number | boolean> = {},
) {
  capturePostHog("client_journey", {
    ...commonProperties(),
    action,
    ...properties,
  });
  captureSemantic("journey_step", { action, ...properties });
}

function activeFeature() {
  return featureForPath(window.location.pathname)?.id ?? "unknown";
}

export function apiOperationContext(): ApiOperationContext {
  const feature = featureForPath(window.location.pathname);
  return {
    initiatingFeatureId: feature?.id ?? "unknown",
    initiatingPathTemplate: feature?.path ?? "unknown",
  };
}

export function captureFeatureView() {
  const feature = featureForPath(window.location.pathname);
  record(feature?.id ?? "unknown", "view", {
    path_template: feature?.path ?? "unknown",
  });
}

export function capturePageLoad(durationMs: number) {
  const feature = featureForPath(window.location.pathname);
  capturePostHog("client_page_load", {
    ...commonProperties(),
    feature_id: feature?.id ?? "unknown",
    path_template: feature?.path ?? "unknown",
    duration_ms: Math.max(0, Math.round(durationMs)),
  });
}

export function captureInteraction(element: Element, interaction: string) {
  const action =
    element.getAttribute("data-telemetry-action") ??
    element.getAttribute("type") ??
    element.tagName.toLowerCase();
  record(activeFeature(), "interaction", { interaction, action });
  captureSemantic("action_completed", {
    action,
    interaction,
    occurrence: actionOccurrence(`${activeFeature()}:${action}:${interaction}`),
  });
}

export function captureNavigation(fromFeatureId: string, durationMs: number) {
  captureSemantic("navigation", {
    from_feature_id: fromFeatureId || "unknown",
    to_feature_id: activeFeature(),
    duration_ms: Math.max(0, Math.round(durationMs)),
  });
}

export function captureEngagement(activeSeconds: number) {
  captureSemantic("engagement_heartbeat", {
    active_seconds: Math.max(0, Math.min(60, Math.round(activeSeconds))),
  });
}

export function captureScrollDepth(percent: number) {
  captureSemantic("scroll_depth", {
    depth_percent: Math.max(0, Math.min(100, Math.round(percent))),
    action: `scroll_${Math.round(percent)}`,
    occurrence: actionOccurrence(
      `${activeFeature()}:scroll_${Math.round(percent)}`,
    ),
  });
}

export function captureClientFailure(kind: string) {
  record(activeFeature(), "failure", { failure_kind: kind });
}

export function captureApiOperation(
  input: RequestInfo | URL,
  response: Response | null,
  startedAt: number,
  method?: string,
  context: ApiOperationContext = apiOperationContext(),
) {
  const url = new URL(
    typeof input === "string"
      ? input
      : input instanceof URL
        ? input.href
        : input.url,
    window.location.origin,
  );
  const dynamicParents = new Set([
    "repositories",
    "scans",
    "tasks",
    "invitations",
    "members",
    "estates",
    "feedback-analyses",
    "archive-uploads",
    "progress",
    "task-catalog",
  ]);
  const normalizedOperation = url.pathname
    .split("/")
    .map((segment, index, segments) =>
      dynamicParents.has(segments[index - 1]) ||
      /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(segment) ||
      segment.length > 48
        ? ":id"
        : segment,
    )
    .join("/");
  // Requests the app shell issues on every route, so their outcome belongs to
  // the shell rather than to whichever feature happened to be on screen.
  const sharedOperations = new Set([
    "/auth/capabilities",
    "/fleets",
    "/estates",
    "/subscriptions/estate",
  ]);
  const status = response?.status ?? 0;
  const statusClass =
    status === 0 ? "network" : `${Math.floor(status / 100)}xx`;
  const failureClass = response?.ok
    ? "none"
    : status === 0
      ? "network"
      : status === 401
        ? "authentication"
        : status === 403
          ? "authorization"
          : status >= 500
            ? "server"
            : status >= 400
              ? "client"
              : "unexpected_response";
  record(activeFeature(), "api", {
    operation: normalizedOperation,
    method:
      method ?? (input instanceof Request ? input.method : undefined) ?? "GET",
    status,
    status_class: statusClass,
    success: response?.ok === true,
    failure_class: failureClass,
    request_scope: sharedOperations.has(normalizedOperation)
      ? "shared_shell"
      : "feature",
    initiating_feature_id: context.initiatingFeatureId,
    initiating_path_template: context.initiatingPathTemplate,
    duration_ms: Math.max(0, Math.round(performance.now() - startedAt)),
  });
}

export function serviceQualityObservations() {
  return Object.values(readObservations());
}
